import React, { FC, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { useAdyenScript } from 'hooks/use-adyen-script';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { getConfigValue } from 'utils/environment';

import theme from '../theme';

import { AdyenAcceptedCards, AdyenLocales, IAdyenCustomCardInputs } from './types';

const AdyenInputsWrapper = styled.div`
  margin-bottom: 1.25rem;
  width: 100%;
  & label {
    > span {
      color: ${theme.adyenInputsWrapperSpan};
    }
    > div > [data-cse^='encrypted'] {
      border: 1px solid ${Styles.color.grey.four};
      min-height: 50px;
      border-radius: ${Styles.borderRadius};
    }
  }
`;

const AdyenCustomCardInputs: FC<IAdyenCustomCardInputs> = ({
  onChange,
  onError,
  onAdyenInputsReady,
}) => {
  // Launches Adyen script/styles to render credit card inputs
  const { loaded } = useAdyenScript();
  const adyenInputs = useRef(null);
  const { language } = useLocale();
  const { formatMessage } = useIntl();
  const currentAdyenLocale = AdyenLocales[language];

  const acceptedAdyenCardTypes =
    useFlag<AdyenAcceptedCards[]>(LaunchDarklyFlag.ADYEN_SUPPORTED_CARD_BRANDS_VARIATIONS) ||
    Object.values(AdyenAcceptedCards);

  // Creates an instance of Adyen Checkout and renders credit card inputs when Adyen script is loaded
  useEffect(() => {
    if (loaded && window.AdyenCheckout) {
      const adyenConfig = getConfigValue('adyen');
      const environment = adyenConfig.environment;
      const clientKey = adyenConfig.clientKey;

      const configuration = {
        locale: currentAdyenLocale,
        translations: {
          [currentAdyenLocale]: {
            'creditCard.numberField.title': formatMessage({ id: 'creditCardNumber' }),
            'creditCard.numberField.invalid': formatMessage({ id: 'ccNumberIsNotValid' }),
            'error.va.sf-cc-num.01': formatMessage({ id: 'ccNumberIsNotValid' }),
            'creditCard.expiryDateField.title': formatMessage({ id: 'cardExpiration' }),
            'creditCard.expiryDateField.placeholder': formatMessage({ id: 'monthYearShort' }),
            'error.va.sf-cc-dat.01': formatMessage({ id: 'creditCardExpired' }),
            'creditCard.cvcField.title': formatMessage({ id: 'cvv' }),
          },
        },
        environment,
        clientKey,
      };
      const checkout = new window.AdyenCheckout(configuration);
      checkout
        .create('card', {
          // accepted card types
          brands: acceptedAdyenCardTypes,
          styles: {
            base: {
              color: Styles.color.black,
              fontSize: '0.9375rem',
              fontFamily: Styles.fontFamily.body,
              fontWeight: Styles.fontWeight.light,
              padding: '0 .5rem',
            },
          },
          /**
           * https://docs.adyen.com/payment-methods/cards/custom-card-integration#show-the-available-cards-in-your-payment-form
           * Using `onConfigSuccess` over `onLoad` because with "onLoad" the cards are not ready to use yet.
           */
          onConfigSuccess: onAdyenInputsReady,
          onChange,
          onError,
        })
        .mount(adyenInputs.current);
    }
  }, [
    loaded,
    currentAdyenLocale,
    onChange,
    onError,
    onAdyenInputsReady,
    formatMessage,
    acceptedAdyenCardTypes,
  ]);

  return <AdyenInputsWrapper ref={adyenInputs} data-testid="adyen-credit-card-fields" />;
};

export default AdyenCustomCardInputs;
